import { db } from '../config/firebase';
import { doc, setDoc, getDoc, collection, addDoc, Timestamp, query, where, getDocs, updateDoc } from 'firebase/firestore';

interface ShopifyConfig {
  shopDomain: string;
  accessToken: string;
  companyId: string;
  syncProducts: boolean;
  syncOrders: boolean;
  orderStatusMappings: {
    lognow: string;
    store: string;
  }[];
}

interface ShopifyProduct {
  id: string;
  title: string;
  description: string;
  price: string;
  images: { src: string }[];
  variants: any[];
  status: string;
  vendor: string;
  product_type: string;
}

interface ApiConfig {
  shopify?: {
    clientId: string;
    clientSecret: string;
  };
}

interface ShopifyTokenResponse {
  access_token: string;
  scope: string;
}

const API_BASE_URL = import.meta.env.PROD ? 'https://www.lognow.pt' : '';

export const shopifyService = {
  async getApiConfig(): Promise<ApiConfig> {
    const configRef = doc(db, 'config', 'api_keys');
    const configSnap = await getDoc(configRef);
    if (!configSnap.exists()) {
      throw new Error('Configuração da API não encontrada');
    }
    return configSnap.data() as ApiConfig;
  },

  async saveShopifyConfig(config: ShopifyConfig) {
    // Salvar apenas na coleção integrations para unificar com outras integrações
    const integrationsRef = collection(db, 'integrations');
    const integrationData = {
      type: 'shopify',
      name: `Shopify (${config.shopDomain})`,
      companyId: config.companyId,
      config: {
        shopDomain: config.shopDomain,
        accessToken: config.accessToken,
        syncProducts: config.syncProducts,
        syncOrders: config.syncOrders,
        orderStatusMappings: config.orderStatusMappings
      },
      active: true,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };
    
    // Verificar se já existe uma integração para este domínio
    const q = query(
      integrationsRef, 
      where('companyId', '==', config.companyId),
      where('type', '==', 'shopify'),
      where('config.shopDomain', '==', config.shopDomain)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      // Criar nova integração
      await addDoc(integrationsRef, integrationData);
    } else {
      // Atualizar integração existente
      const docRef = doc(db, 'integrations', querySnapshot.docs[0].id);
      await updateDoc(docRef, {
        ...integrationData,
        updatedAt: Timestamp.now()
      });
    }
  },

  async getShopifyConfig(companyId: string): Promise<ShopifyConfig | null> {
    try {
      // Buscar da coleção integrations
      const integrationsRef = collection(db, 'integrations');
      const q = query(
        integrationsRef,
        where('companyId', '==', companyId),
        where('type', '==', 'shopify')
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }
      
      const integrationData = querySnapshot.docs[0].data();
      const config = integrationData.config;
      
      return {
        shopDomain: config.shopDomain,
        accessToken: config.accessToken,
        companyId: integrationData.companyId,
        syncProducts: config.syncProducts || true,
        syncOrders: config.syncOrders || true,
        orderStatusMappings: config.orderStatusMappings || []
      };
    } catch (error) {
      console.error('Erro ao buscar configuração do Shopify:', error);
      return null;
    }
  },

  async generateAuthUrl(companyId: string | 'default', shopDomain: string): Promise<string> {
    const apiConfig = await this.getApiConfig();
    if (!apiConfig.shopify?.clientId) {
      throw new Error('Client ID do Shopify não configurado');
    }

    // Se não tiver companyId, usa 'default' como state
    const state = companyId === 'default' ? 'default' : companyId;

    // Garantir que temos o domínio completo da loja
    const fullShopDomain = shopDomain.includes('.myshopify.com') ? shopDomain : `${shopDomain}.myshopify.com`;

    const redirectUri = 'https://www.lognow.pt/dashboard/multicanal/shopify/callback';
    const scopes = 'read_products,write_products,read_orders,write_orders';
    
    return `https://${fullShopDomain}/admin/oauth/authorize?` +
      `client_id=${apiConfig.shopify.clientId}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `scope=${encodeURIComponent(scopes)}&` +
      `state=${encodeURIComponent(state)}`;
  },

  async exchangeCodeForToken(code: string, shopDomain: string, companyId: string, hmac: string): Promise<ShopifyTokenResponse> {
    console.log('Dados enviados para a API:', { code, shop: shopDomain, companyId, hmac });

    try {
      // Chamada para nossa API backend que vai buscar as credenciais do Shopify no Firebase
      const response = await fetch('/api/shopify/exchange-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code,
          shop: shopDomain,
          companyId,
          hmac
        }),
      });

      // Verificar status da resposta
      console.log('Status da resposta:', response.status);
      console.log('Headers da resposta:', response.headers);

      if (!response.ok) {
        const errorData = await response.text();
        console.log('Erro detalhado:', { status: response.status, statusText: response.statusText, body: errorData });
        throw new Error(`Erro ao trocar código por token: ${response.status}`);
      }

      const data = await response.json();
      console.log('Resposta da troca de token:', { 
        access_token: data.access_token ? 'Presente' : 'Ausente',
        scope: data.scope
      });
      
      // Armazenar as configurações
      await this.saveShopifyConfig({
        shopDomain,
        accessToken: data.access_token,
        companyId,
        syncProducts: true,
        syncOrders: true,
        orderStatusMappings: [
          { lognow: 'pending', store: 'pending' },
          { lognow: 'processing', store: 'processing' },
          { lognow: 'completed', store: 'completed' },
          { lognow: 'cancelled', store: 'cancelled' }
        ]
      });

      return data;
    } catch (error) {
      console.log('Erro completo:', error);
      throw error;
    }
  },

  async getShopifyProducts(config: ShopifyConfig): Promise<ShopifyProduct[]> {
    try {
      // Fazer chamada para nossa API backend em vez de chamar diretamente o Shopify
      // Isso evita problemas de CORS
      const response = await fetch('/api/shopify/get-products', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          shopDomain: config.shopDomain,
          accessToken: config.accessToken,
          companyId: config.companyId
        }),
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error('Erro detalhado:', { status: response.status, statusText: response.statusText, body: errorData });
        throw new Error(`Erro ao obter produtos do Shopify: ${response.status}`);
      }

      const data = await response.json();
      return data.products || [];
    } catch (error) {
      console.error('Erro ao buscar produtos do Shopify:', error);
      throw error;
    }
  },

  async importProducts(products: any[], companyId: string) {
    try {
      const response = await fetch('/api/shopify/import-products', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          products,
          companyId
        }),
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error('Erro detalhado:', { status: response.status, statusText: response.statusText, body: errorData });
        throw new Error(`Erro ao importar produtos: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Erro ao importar produtos:', error);
      const errorDetails = error.response?.data || error.message;
      console.error('Erro detalhado:', errorDetails);
      throw new Error(`Erro ao importar produtos: ${error.response?.status || error.message}`);
    }
  }
};
